import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fget, fpost } from 'src/lib/fetch';
import { useMobileViewport } from 'src/modules/map/hooks/useMobileViewport';
import { selectSelectedArea, selectSelectedRiverNames } from 'src/redux/slices/area.slice';
import styles from './MapRiverDashboard.module.css';

interface DeviceDocument {
  DEVICE_ID?: { S?: string };
  AREA_NAME?: { S?: string };
  RIVER_NAME?: { S?: string };
  WATER_LEVEL_THRESHOLD?: { S?: string };
}

interface RiverSummaryItem {
  riverName: string;
  areaName: string;
  averageWaterLevel: number;
  averageFlowRate: number;
  deviceCount: number;
  activeDeviceCount: number;
  activeRate: number;
  latestObservedAt: string;
  thresholdExceededCount: number;
}

const staleLimitMs = 30 * 60 * 1000;

const parsePublishTime = (value?: string) => {
  if (!value?.trim()) return null;
  const raw = value.trim();

  if (/^\d+$/.test(raw)) {
    const timestamp = Number(raw);
    const date = new Date(raw.length <= 10 ? timestamp * 1000 : timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw.replace(/\//g, '-').replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
};

const toNumber = (value?: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isActiveRecord = (publish?: string) => {
  const parsed = parsePublishTime(publish);
  return parsed ? Date.now() - parsed.getTime() < staleLimitMs : false;
};

const clampPercentage = (value: number) => Math.max(0, Math.min(100, value));

function DonutChart({ percentage }: { percentage: number }) {
  const safePercentage = clampPercentage(percentage);
  const radius = 34;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className={styles.donutWrap}>
      <svg viewBox="0 0 100 100" className={styles.donutChart} aria-hidden="true">
        <circle className={styles.donutTrack} cx="50" cy="50" r={radius} />
        <circle
          className={styles.donutValue}
          cx="50"
          cy="50"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (safePercentage / 100) * circumference}
        />
      </svg>
      <div className={styles.donutCenter}>
        <strong>{safePercentage.toFixed(0)}</strong>
        <span>%</span>
      </div>
    </div>
  );
}

function BarGraph({
  label,
  value,
  unit,
  maxValue,
  tone,
}: {
  label: string;
  value: number;
  unit: string;
  maxValue: number;
  tone: 'blue' | 'green';
}) {
  const percentage = clampPercentage((Math.max(value, 0) / (maxValue > 0 ? maxValue : 1)) * 100);

  return (
    <div className={styles.metricCard}>
      <div className={styles.metricHeader}>
        <span className={styles.metricLabel}>{label}</span>
        <span className={styles.metricValue}>
          {value.toFixed(2)} {unit}
        </span>
      </div>
      <div className={styles.barTrack}>
        <div
          className={`${styles.barFill} ${tone === 'blue' ? styles.barBlue : styles.barGreen}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export function MapRiverDashboard() {
  const selectedArea = useSelector(selectSelectedArea);
  const selectedRiverNames = useSelector(selectSelectedRiverNames);
  const isMobileViewport = useMobileViewport();
  const [isOpen, setIsOpen] = useState(() => !window.matchMedia('(max-width: 768px)').matches);
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<RiverSummaryItem[]>([]);

  useEffect(() => {
    setIsOpen(!isMobileViewport);
  }, [isMobileViewport]);

  useEffect(() => {
    document.body.classList.toggle('map-river-dashboard-open', isOpen);
    window.dispatchEvent(new Event('map-layout-change'));

    return () => {
      document.body.classList.remove('map-river-dashboard-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const areaName = selectedArea?.AREA_NAME?.S;
    if (!areaName) {
      setItems([]);
      return;
    }

    let cancelled = false;
    const fetchSummary = async () => {
      setIsLoading(true);
      try {
        const response = await fget('api/devices');
        const devices = (response?.[1]?.Items || []) as DeviceDocument[];
        const riverNameSet = new Set(selectedRiverNames);
        const filteredDevices = devices.filter((device) => {
          const deviceAreaName = device.AREA_NAME?.S || '';
          const riverName = device.RIVER_NAME?.S || '';
          return deviceAreaName === areaName || riverNameSet.has(riverName);
        });

        const latestRecords = (
          await Promise.allSettled(
            filteredDevices.map(async (device) => {
              const deviceId = device.DEVICE_ID?.S;
              if (!deviceId) return null;

              const query = {
                query: {
                  ExpressionAttributeValues: { ':id': { S: deviceId } },
                  KeyConditionExpression: 'DEVICE_ID = :id',
                  TableName: 'DEVICE_RECORD',
                  ScanIndexForward: false,
                  Limit: 1,
                },
              };
              const recordResponse = await fpost<any>('api/device/record/query', { body: query });
              return { device, record: recordResponse?.[1]?.Items?.[0] };
            }),
          )
        )
          .map((result) => (result.status === 'fulfilled' ? result.value : null))
          .filter(Boolean);

        const summaryMap = new Map<
          string,
          {
            areaName: string;
            waterLevelTotal: number;
            flowRateTotal: number;
            deviceCount: number;
            activeDeviceCount: number;
            latestObservedAt: string;
            latestObservedAtTs: number;
            thresholdExceededCount: number;
          }
        >();

        latestRecords.forEach((entry) => {
          if (!entry) return;

          const riverName = entry.device.RIVER_NAME?.S || '未設定河川';
          const waterLevel = toNumber(entry.record?.WATER_LEVEL?.S);
          const flowRate = toNumber(entry.record?.FLOW_RATE?.S);
          const threshold = toNumber(entry.device.WATER_LEVEL_THRESHOLD?.S);
          const publish =
            entry.record?.Publish?.S || entry.record?.PUBLISH?.S || entry.record?.PUBLISH_FORMATED?.S || '';
          const publishTs = parsePublishTime(publish)?.getTime() || 0;
          const current = summaryMap.get(riverName) || {
            areaName: entry.device.AREA_NAME?.S || areaName,
            waterLevelTotal: 0,
            flowRateTotal: 0,
            deviceCount: 0,
            activeDeviceCount: 0,
            latestObservedAt: '',
            latestObservedAtTs: 0,
            thresholdExceededCount: 0,
          };

          current.waterLevelTotal += waterLevel;
          current.flowRateTotal += flowRate;
          current.deviceCount += 1;
          if (isActiveRecord(publish)) current.activeDeviceCount += 1;
          if (publishTs >= current.latestObservedAtTs) {
            current.latestObservedAt = publish || '未取得';
            current.latestObservedAtTs = publishTs;
          }
          if (threshold > 0 && waterLevel > threshold) current.thresholdExceededCount += 1;
          summaryMap.set(riverName, current);
        });

        const riverOrder = selectedRiverNames.length > 0 ? selectedRiverNames : Array.from(summaryMap.keys());
        const nextItems = riverOrder
          .filter((riverName) => summaryMap.has(riverName))
          .map((riverName) => {
            const item = summaryMap.get(riverName);
            if (!item) return null;
            return {
              riverName,
              areaName: item.areaName,
              averageWaterLevel: item.waterLevelTotal / item.deviceCount,
              averageFlowRate: item.flowRateTotal / item.deviceCount,
              deviceCount: item.deviceCount,
              activeDeviceCount: item.activeDeviceCount,
              activeRate: (item.activeDeviceCount / item.deviceCount) * 100,
              latestObservedAt: item.latestObservedAt || '未取得',
              thresholdExceededCount: item.thresholdExceededCount,
            };
          })
          .filter((item): item is RiverSummaryItem => Boolean(item));

        if (!cancelled) setItems(nextItems);
      } catch (error) {
        console.error(error);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [selectedArea, selectedRiverNames]);

  const areaLabel = selectedArea?.AREA_NAME?.S || '対象地域';
  const maxWaterLevel = useMemo(() => Math.max(1, ...items.map((item) => item.averageWaterLevel)), [items]);
  const maxFlowRate = useMemo(() => Math.max(1, ...items.map((item) => item.averageFlowRate)), [items]);

  return (
    <div className={styles.root}>
      <aside className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}>
        <div className={styles.header}>
          <h2 className={styles.title}>対象河川ダッシュボード</h2>
          <p className={styles.subtitle}>{areaLabel}</p>
        </div>

        <div className={styles.body}>
          {isLoading && items.length === 0 && <div className={styles.stateText}>読み込み中</div>}
          {!isLoading && items.length === 0 && (
            <div className={styles.stateText}>表示できる河川データがありません</div>
          )}

          {items.map((item) => (
            <section key={`${item.areaName}-${item.riverName}`} className={styles.card}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.riverName}>{item.riverName}</h3>
                  <p className={styles.areaName}>{item.areaName}</p>
                </div>
                <span className={styles.deviceCount}>{`${item.deviceCount}台`}</span>
              </div>

              <div className={styles.chartGrid}>
                <div className={styles.rateCard}>
                  <div className={styles.metricLabel}>稼働デバイス割合</div>
                  <DonutChart percentage={item.activeRate} />
                  <div className={styles.rateMeta}>{`${item.activeDeviceCount}/${item.deviceCount} 台が30分以内に更新`}</div>
                </div>
                <BarGraph label="平均流量" value={item.averageFlowRate} unit="m3/s" maxValue={maxFlowRate} tone="green" />
                <BarGraph label="平均水位" value={item.averageWaterLevel} unit="m" maxValue={maxWaterLevel} tone="blue" />
              </div>

              <dl className={styles.summaryGrid}>
                <div>
                  <dt>最終観測</dt>
                  <dd>{item.latestObservedAt}</dd>
                </div>
                <div>
                  <dt>閾値超過</dt>
                  <dd>{`${item.thresholdExceededCount}件`}</dd>
                </div>
              </dl>
            </section>
          ))}
        </div>
      </aside>

      <button
        type="button"
        className={styles.toggle}
        aria-label={isOpen ? '対象河川ダッシュボードを閉じる' : '対象河川ダッシュボードを開く'}
        onClick={() => setIsOpen((current) => !current)}>
        <svg
          className={styles.toggleIcon}
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" />
          {isOpen ? (
            <>
              <polyline points="11 7 6 12 11 17" />
              <polyline points="17 7 12 12 17 17" />
            </>
          ) : (
            <>
              <polyline points="7 7 12 12 7 17" />
              <polyline points="13 7 18 12 13 17" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
}
